import { useMutation } from '@tanstack/react-query';

import { getRuntimeConfig } from '@nucleus-commerce/admin-kit/config';

import { applyTryItResponse, updateDocsSession } from '../api/docs-session';
import { buildTryItRequest, sendTryIt, type TryItRequest } from '../api/try-it-client';
import type { DocsOperation } from '../generated/reference';
import type { TryItValues } from '../schemas/try-it-form-schema';

import { useDocsKeys } from './use-docs-keys';
import { useDocsSession } from './use-docs-session';

/**
 * Sends one operation with the selected key and the tab's customer token, and feeds the response
 * back into the docs session. The panel renders HTTP errors itself; only a request that never got
 * an answer ends up in `error`.
 */
export function useTryIt(operation: DocsOperation) {
  const { selectedKey } = useDocsKeys();
  const { customer } = useDocsSession();
  const mutation = useMutation({
    mutationFn: sendTryIt,
    meta: { silentError: true },
    onSuccess: (response, request) =>
      updateDocsSession((session) => applyTryItResponse(session, operation, request, response)),
  });

  const buildRequest = (values: TryItValues): TryItRequest | null =>
    selectedKey
      ? buildTryItRequest(operation, values, {
          baseUrl: getRuntimeConfig().apiBaseUrl,
          apiKey: selectedKey.key,
          customerToken: customer?.accessToken ?? null,
        })
      : null;

  return {
    canSend: selectedKey !== null,
    buildRequest,
    send: (values: TryItValues) => {
      const request = buildRequest(values);
      if (request) mutation.mutate(request);
    },
    isPending: mutation.isPending,
    response: mutation.data ?? null,
    error: mutation.error,
  };
}
